import { useEffect, useState } from 'react';

import { PlusIcon, SaveIcon } from 'lucide-react';
import { type DeepPartial } from 'react-hook-form';

import {
  type FormDefinitionType,
  type NonInternalProps,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

type ActivityDialogShellProps<TFormValues extends Record<string, unknown>> = {
  open: boolean;
  title: string;
  defaultValues: DeepPartial<TFormValues>;
  formDefinition: FormDefinitionType<TFormValues>;
  onClose: () => void;
  onCreate?: (values: NonInternalProps<TFormValues>) => void | Promise<void>;
  onChange?: RaFormOnChange<TFormValues>;
  mode?: 'create' | 'edit';
  submitButtonText?: string;
  isLoadingData?: boolean;
};

export const ActivityDialogShell = <
  TFormValues extends Record<string, unknown>,
>({
  open,
  title,
  defaultValues,
  formDefinition,
  onClose,
  onCreate,
  onChange,
  mode = 'create',
  submitButtonText,
  isLoadingData = false,
}: ActivityDialogShellProps<TFormValues>) => {
  const { t } = useLocale();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formInstanceKey, setFormInstanceKey] = useState(0);

  useEffect(() => {
    if (open) {
      setFormInstanceKey(prev => prev + 1);
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    onClose();
  };

  const handleSubmit = async (values: NonInternalProps<TFormValues>) => {
    if (!onCreate) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreate(values);
      onClose();
    } catch (error) {
      console.error(error);
      throw error; // Re-throw so RaForm can handle it
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolvedSubmitText =
    submitButtonText ?? (mode === 'edit' ? t('Save') : t('Create'));
  const resolvedSubmitIcon = mode === 'edit' ? <SaveIcon /> : <PlusIcon />;

  return (
    <SlideDialog
      open={open}
      onClose={handleClose}
      title={title}
      loading={isSubmitting || isLoadingData}
      footerActions={<></>}
    >
      {isLoadingData ? (
        <LoadingSpinner />
      ) : (
        <RaForm
          key={formInstanceKey}
          formDefinition={formDefinition}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onChange={onChange}
          onCancel={handleClose}
          allowSubmitNotDirty
          submitButtonText={resolvedSubmitText}
          cancelButtonText={t('Cancel')}
          actionButtonsSlotProps={{
            container: {
              elevation: 0,
              sx: theme => ({
                borderTop: `1px solid ${(theme.vars ?? theme).palette.divider}`,
                backgroundColor: (theme.vars ?? theme).palette.background.paper,
              }),
            },
            submitButtonProps: {
              startIcon: resolvedSubmitIcon,
              loadingPosition: 'start',
            },
          }}
        />
      )}
    </SlideDialog>
  );
};
